import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useDealershipData } from '@/data/hooks/useDealershipData';

export interface RepStreaks {
  current_streak: number;
  longest_streak: number;
  followups_today: number;
  last_activity_date: string | null;
}

const EMPTY: RepStreaks = {
  current_streak: 0,
  longest_streak: 0,
  followups_today: 0,
  last_activity_date: null,
};

const FOLLOWUP_TYPES = new Set(['text_sent', 'email_sent', 'phone_call', 'followup_video', 'note_added']);

/**
 * Real mode → reads `rep_streaks` (kept fresh by trigger).
 * Demo mode → derives streak data from in-memory mock activities.
 */
export function useRepStreaks() {
  const { mode, userId } = useAuth();
  const { activities } = useDealershipData();
  const [streaks, setStreaks] = useState<RepStreaks>(EMPTY);
  const [loading, setLoading] = useState(true);

  const repId = mode === 'demo' ? 'rep-alex' : userId;

  const load = useCallback(async () => {
    if (mode === 'demo') {
      const mine = activities.filter((a) => a.rep_id === repId);
      const today = new Date().toISOString().slice(0, 10);
      const todayCount = mine.filter(
        (a) => a.created_at.slice(0, 10) === today && FOLLOWUP_TYPES.has(a.activity_type),
      ).length;
      setStreaks({
        current_streak: mine.length > 0 ? 3 : 0,
        longest_streak: 7,
        followups_today: todayCount,
        last_activity_date: today,
      });
      setLoading(false);
      return;
    }
    if (!userId) {
      setStreaks(EMPTY);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from('rep_streaks' as never)
      .select('current_streak, longest_streak, followups_today, last_activity_date')
      .eq('user_id', userId)
      .maybeSingle();
    const row = data as RepStreaks | null;
    setStreaks(row ?? EMPTY);
    setLoading(false);
  }, [mode, userId, repId, activities]);

  useEffect(() => { load(); }, [load]);

  return { streaks, loading, refresh: load };
}